import React from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import IdChip from '../payloadNew/IdChip';
import { Icons } from '../payloadNew/Icons';
import { epochToLocalDate } from '../../utils/dateConverter';

// Local Stat helper mirroring the one used inline by PayloadNew. Kept here so
// SurveyData stays self-contained until a shared primitive is extracted.
const Stat = ({ label, value, mono }) => {
	const isMissing = value === undefined
		|| value === null
		|| value === ''
		|| value === 'No information'
		|| value === 'Invalid date';
	return (
		<div>
			<div className='pn-stat-label'>{label}</div>
			<div className={`pn-stat-value ${mono ? 'mono' : ''} ${isMissing ? 'muted' : ''}`}>
				{isMissing ? '—' : String(value)}
			</div>
		</div>
	);
};

Stat.propTypes = {
	label: PropTypes.string,
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	mono: PropTypes.bool,
};

/**
 * Survey "Response Information" card. Visually matches PayloadNew's
 * Payload Information section (header with icon + title + IdChip, then a
 * 3-column stat grid). Must be rendered inside a wrapper carrying the
 * `.payload-new` class so the `.pn-*` styles and design tokens apply.
 */
const SurveyData = ({ info, sectionId, schemaLabel }) => {
	const { t } = useTranslation();

	const responseId = info?._id?.$oid;
	const createdAt = info?.date?.$date
		? epochToLocalDate(info.date.$date, localStorage.getItem('i18nextLng'))
		: '';

	// Callers can override the schema display value (e.g. to apply an alias
	// from the org's app-config). Falls back to the raw schema name when no
	// override is supplied.
	const schemaValue = schemaLabel !== undefined ? schemaLabel : info?.survey_schema?.name;

	return (
		<section id={sectionId} className='pn-card'>
			<div className='pn-section-header'>
				<h2 className='pn-section-title'>
					<Icons.phone /> {t('surveys.response.info.title')}
				</h2>
				{responseId && <IdChip label='Id' value={responseId} full />}
			</div>
			<div className='pn-stat-grid cols-3'>
				<Stat label={t('surveys.response.info.schema')} value={schemaValue} />
				<Stat label={t('surveys.response.info.user')} value={info?.user?.username} mono />
				{info?.workspace && (
					<Stat label={t('surveys.response.info.workspace')} value={info?.workspace?.name} />
				)}
				<Stat label={t('surveys.response.info.creationDate')} value={createdAt} mono />
			</div>
		</section>
	);
};

SurveyData.propTypes = {
	info: PropTypes.object.isRequired,
	sectionId: PropTypes.string,
	schemaLabel: PropTypes.string,
};

export default SurveyData;
